import display from 'assets/campaign/adType-display.png';
import display2x from 'assets/campaign/adType-display@2x.png';
import {
  AdType,
  AD_TYPE_MAP_CREATIVE_TYPE,
} from 'core/rtbCampaign/RtbCampaign';
import {
  DefaultCampaignAdTypeButtonModel,
  CampaignAdTypeButtonModel,
} from '../CampaignAdTypeButton/CampaignAdTypeButtonModel';
import {
  UpdateEventListener,
  FireableUpdateEventListener,
} from 'utils/UpdateEventListener';
import { ADDONFEATURE, AddonFeatureType } from 'core/agency/AddonFeature';
import { RtbCampaignSetupFlowPageModel } from '../RtbCampaignSetupFlowPageModel';
import i18n from 'i18next';
import _ from 'lodash';
import { L1ObjectChannel } from 'core/l1Object/L1Object';
import { DefaultBrandManager } from 'core/brand/BrandManager';
import { userStore } from 'store/user';

export const CHANNEL_MAP_CAMPAIGN_TYPE = {
  [L1ObjectChannel.RTB]: [
    AdType.DISPLAY,
    AdType.KEYWORD,
    AdType.SMART_KEYWORD,
    AdType.MAX_CVR_KEYWORD,
    AdType.SMART_CONFIG,
    AdType.SHOP,
  ],
};

export interface ChooseAdTypeStepModel {
  readonly event: UpdateEventListener<ChooseAdTypeStepModel>;
  readonly state: ChooseAdTypeStepState;
  readonly flowModel: RtbCampaignSetupFlowPageModel;
  readonly getAdTypeButtonModels: () => CampaignAdTypeButtonModel[];
  readonly showChangeAdTypeAlertModal: (confirmFuc) => void;
  fetchBrandOptions(): Promise<void>;
  onAdTypeChange: (adType: AdType) => void;
  backToOrderDetail: () => void;
}

export type ChooseAdTypeStepProps = {
  readonly model: ChooseAdTypeStepModel;
};

export type ChooseAdTypeStepState = {
  readonly changeAdTypeAlertModal?: any;
  loading: boolean;
  hasBrand?: boolean;
};

export class DefaultChooseAdTypeStepModel implements ChooseAdTypeStepModel {
  event: FireableUpdateEventListener<ChooseAdTypeStepModel>;
  adType: AdType;
  changeAdTypeAlertModal?: any;
  flowModel: RtbCampaignSetupFlowPageModel;
  loading: boolean;
  hasBrand?: boolean;
  userStore: any;

  constructor(
    flowModel: RtbCampaignSetupFlowPageModel,
    private goNext: () => void,
    private defaultBrandManager: DefaultBrandManager = new DefaultBrandManager(),
    hasBrand?: boolean,
  ) {
    this.flowModel = flowModel;
    this.adType = flowModel.state.campaign.basic.adType;
    this.event = new FireableUpdateEventListener<ChooseAdTypeStepModel>();
    this.loading = false;
    this.userStore = userStore;
    this.hasBrand = hasBrand;
  }

  get state() {
    return {
      changeAdTypeAlertModal: this.changeAdTypeAlertModal,
      loading: this.loading,
      adType: this.adType,
      hasBrand: this.hasBrand,
    };
  }

  get adTypeOptions() {
    const addonFeatureManager = this.flowModel.addonFeatureManager;
    const channel = L1ObjectChannel.RTB;
    const canSelectSmartConfig = _.get(this.flowModel, 'canSelectSmartConfig');
    const allowAdTypes = {
      ...AD_TYPE_MAP_CREATIVE_TYPE,
    };
    return Object.keys(allowAdTypes).reduce((allowAdType, adType) => {
      const allowCreativeTypes = allowAdTypes[adType];
      let hasPermission = allowCreativeTypes.some((creativeType) => {
        return addonFeatureManager.isFeatureEnable(
          `option_${creativeType}` as AddonFeatureType,
        );
      });
      if (adType === AdType.SMART_KEYWORD) {
        hasPermission =
          hasPermission &&
          addonFeatureManager.isFeatureEnable(
            ADDONFEATURE.CAMPAIGN.SMART_KEYWORD,
          );
      }
      if (adType === AdType.MAX_CVR_KEYWORD) {
        hasPermission =
          hasPermission &&
          addonFeatureManager.isFeatureEnable(
            ADDONFEATURE.CAMPAIGN.MAX_CVR_KEYWORD,
          );
      }
      if (adType === AdType.SHOP) {
        hasPermission =
          hasPermission &&
          addonFeatureManager.isFeatureEnable(ADDONFEATURE.CAMPAIGN.SHOP);
      }
      const channelCompatible =
        !(channel in CHANNEL_MAP_CAMPAIGN_TYPE) ||
        CHANNEL_MAP_CAMPAIGN_TYPE[channel]
          .map((adType) => adType.toString())
          .includes(adType);
      if (adType === AdType.SMART_CONFIG && !canSelectSmartConfig) {
        return allowAdType;
      }
      if (hasPermission && channelCompatible) {
        allowAdType.push(AdType[adType]);
      }
      return allowAdType;
    }, [] as Array<AdType>);
  }
  // 生成活動類型
  getAdTypeButtonModels = () => {
    return this.adTypeOptions.map((adTypeOption) => {
      return new DefaultCampaignAdTypeButtonModel(
        this.getAdTypeButtonData(adTypeOption),
        adTypeOption === this.adType,
        _.partial(this.onAdTypeButtonClick, adTypeOption),
      );
    });
  };

  onAdTypeButtonClick = (adType: AdType) => {
    const campaign = this.flowModel.state.campaign;
    const currentAdType = _.get(campaign, 'basic.adType');
    if (currentAdType !== undefined && adType !== currentAdType) {
      this.showChangeAdTypeAlertModal(() => {
        this.onAdTypeChange(adType);
      });
    } else {
      this.onAdTypeChange(adType);
    }
  };

  onAdTypeChange(adType: AdType) {
    this.adType = adType;
    this.flowModel.onAdTypeChange(adType);
    this.goNext();
  }

  getAdTypeButtonData(adTypeOption: AdType) {
    switch (adTypeOption) {
      case AdType.DISPLAY:
        return {
          icon: display,
          icon2x: display2x,
          title: 'campaign.buttons.adTypeDisplayTitle',
          description: 'campaign.buttons.adTypeDisplayDesc',
          type: AdType.DISPLAY,
        };
      case AdType.KEYWORD:
        return {
          icon: display,
          icon2x: display2x,
          title: 'campaign.buttons.adTypeKeywordTitle',
          description: 'campaign.buttons.adTypeKeywordDesc',
          type: AdType.KEYWORD,
        };
      case AdType.SMART_KEYWORD:
        return {
          icon: display,
          icon2x: display2x,
          title: 'campaign.buttons.adTypeSmartKeywordTitle',
          description: 'campaign.buttons.adTypeSmartKeywordDesc',
          type: AdType.SMART_KEYWORD,
        };
      case AdType.MAX_CVR_KEYWORD:
        return {
          icon: display,
          icon2x: display2x,
          title: 'campaign.buttons.adTypeMaxCVRKeywordTitle',
          description: 'campaign.buttons.adTypeSmartKeywordDesc',
          type: AdType.MAX_CVR_KEYWORD,
        };
      case AdType.SMART_CONFIG:
        return {
          icon: display,
          icon2x: display2x,
          title: 'campaign.buttons.adTypeSmartConfigTitle',
          description: 'campaign.buttons.adTypeSmartConfigDesc',
          type: AdType.SMART_CONFIG,
        };
      case AdType.SHOP:
        return {
          icon: display,
          icon2x: display2x,
          title: 'campaign.buttons.adTypeShopTitle',
          description: userStore.getState().hasBrand
            ? 'campaign.buttons.adTypeShopDesc'
            : 'campaign.buttons.adTypeShopDescNoBrand',
          type: AdType.SHOP,
        };
      default:
        return {
          icon: display,
          icon2x: display2x,
          title: 'campaign.buttons.adTypeDisplayTitle',
          description: 'Image / Product Native ',
          type: AdType.DISPLAY,
        };
    }
  }

  showChangeAdTypeAlertModal = (confirmFuc) => {
    this.changeAdTypeAlertModal = {
      title: i18n.t<string>('common.warning'),
      message: i18n.t<string>('campaign.descriptions.adTypeChangeAlert'),
      primaryBtn: {
        title: i18n.t<string>('common.buttons.ok'),
        callback: () => {
          confirmFuc();
          this.hideModal();
        },
      },
      secondaryBtn: {
        title: i18n.t<string>('common.buttons.cancel'),
        callback: this.hideModal,
      },
    };
    this.updateState(false);
  };

  hideModal = () => {
    this.changeAdTypeAlertModal = undefined;
    this.updateState(false);
  };

  backToOrderDetail = () => {
    this.flowModel.setFinishedRedirectPath(
      `/orders/${this.flowModel.order.orderNumber}`,
    );
  };

  updateState(loading: boolean) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
  handleSetHasBrand = (check: boolean) => {
    const { setHasBrand } = this.userStore.getState();
    setHasBrand(check);
  };
  fetchBrandOptions = async (): Promise<void> => {
    this.updateState(true);
    try {
      const brandOptions = await this.defaultBrandManager.getBrandOptions();
      this.handleSetHasBrand(brandOptions.length > 0 ? true : false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      this.updateState(false);
    }
  };
}
